<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchRestriction,
  createRestriction,
  updateRestriction,
  fetchRestrictionTargets,
  fetchRestrictionTarget,
  createRestrictionTarget,
  updateRestrictionTarget,
  deleteRestrictionTarget,
} from '@apis/admin/security'
import { fetchAssets } from '@apis/admin/asset'
import LangItem from '@components/admin/lang-input'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'

export default {
  components: { LangItem },
  props: {
    restriction_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      info: null,
      restriction_targets: [],
      assets: [],
      target_params: null,
      loading: false,
      active_tab: 'info',
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      Promise.all([this.loadRestriction(), this.loadAssets()])
        .then(() => this.loadRestrictionTargets())
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadRestriction() {
      const { restriction_info } = await fetchRestriction(this.restriction_seq)
      this.params = defaults(restriction_info, {
        restriction: 0,
        title: '',
        description: '',
        list_order: 0,
        seq: null,
        regdate: null,
      })
      this.info = restriction_info
    },
    async loadRestrictionTargets() {
      if (!this.info) return []

      const { data } = await fetchRestrictionTargets(this.info.seq)

      this.restriction_targets = data
    },
    async loadRestrictionTarget(target_seq) {
      const { data } = await fetchRestrictionTarget(this.info.seq, target_seq)

      this.target_params = data
    },
    async loadAssets() {
      const { data } = await fetchAssets()

      this.assets = data
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq
        ? updateRestriction
        : createRestriction
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t('admin.msg_restriction_modify')
        : this.$t('admin.msg_restriction_enroll')

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() => createOrUpdate(this.params))
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    handleAddTarget() {
      this.restriction_targets.push({
        list_order: 0,
        restriction_seq: this.info.seq,
        target_type: undefined,
        amount: 0,
        asset_seq: undefined,
      })
    },
    async handleDeleteTarget(idx) {
      this.loading = true
      try {
        if (this.restriction_targets[idx].seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deleteRestrictionTarget(
            this.info.seq,
            this.restriction_targets[idx].seq
          )
        }

        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_delete'),
          type: 'success',
        })
        this.restriction_targets.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleSubmitTarget(idx) {
      this.loading = true
      const params = this.restriction_targets[idx]
      ;(params.seq ? updateRestrictionTarget : createRestrictionTarget)(params)
        .then(({ seq }) => fetchRestrictionTarget(this.info.seq, seq))
        .then(({ target_info }) => {
          this.$notify({
            title: this.$t('toasted_msg.title'),
            message: params.seq
              ? this.$t('admin.msg_save_success')
              : this.$t('admin.msg_enroll_success'),
            type: 'success',
          })
          this.$set(this.restriction_targets, idx, { ...target_info })
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <el-tabs v-model="active_tab" :stretch="true">
    <el-tab-pane :label="$t('admin.default_info')" name="info">
      <form
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleSubmit"
      >
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.restriction_title') }}</div></th
              >
              <td
                ><LangItem v-if="!loading" v-model="params.title"></LangItem
              ></td>
              <th scope="row"
                ><div>{{ $t('admin.description') }}</div></th
              >
              <td
                ><LangItem
                  v-if="!loading"
                  v-model="params.description"
                ></LangItem
              ></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.level_manage') }}</div></th
              >
              <td
                ><input
                  v-model="params.level_start"
                  type="text"
                  :class="[$style.inputTypeText, $style.w40, $style.tRight]"
                />
                ~
                <input
                  v-model="params.level_end"
                  type="text"
                  :class="[$style.inputTypeText, $style.w40, $style.tRight]"
                />
              </td>
              <th scope="row"
                ><div>{{ $t('admin.restriction_list_order') }}</div></th
              >
              <td
                ><input
                  v-model="params.list_order"
                  type="text"
                  :class="[$style.inputTypeText, $style.w40, $style.tRight]"
              /></td>
            </tr>
            <tr>
              <th colspan="4" :class="$style.button">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!info || !info.seq"
      :label="$t('admin.restriction_target')"
      name="TARGET"
    >
      <table
        v-if="info && info.seq"
        cellspacing="0"
        cellpadding="0"
        :class="$style.colTable"
      >
        <thead>
          <tr>
            <th scope="col">{{ $t('admin.level_list_order') }}</th>
            <th scope="col">{{ $t('admin.restriction_apply') }}</th>
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.black, $style.button]"
                  @click="handleAddTarget"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(target, idx) in restriction_targets"
            :key="target.seq ? `${target.seq}` : `create-${idx}`"
          >
            <td :class="$style.tCenter">
              <input
                v-model="target.list_order"
                type="text"
                :class="[$style.inputTypeText, $style.w40, $style.tRight]"
              />
            </td>
            <td :class="$style.wide">
              <i18n
                :path="
                  `admin.restriction_target_list.${target.target_type ||
                    'NONE'}`
                "
                tag="span"
              >
                <template v-if="target.target_type">
                  <select v-model="target.asset_seq">
                    <option :value="undefined">{{
                      $t('admin.restriction_select')
                    }}</option>
                    <option
                      v-for="{ seq, title } in assets"
                      :key="seq"
                      :value="seq"
                    >
                      {{ title }}
                    </option>
                  </select>

                  <template v-if="target.target_type !== 'CREATE_ACCOUNT'">
                    <input
                      v-model="target.amount"
                      type="text"
                      :class="[
                        $style.inputTypeText,
                        $style.w150,
                        $style.tRight,
                      ]"
                    />
                  </template>
                </template>
                <select v-model="target.target_type" :disabled="target.seq">
                  <option :value="undefined">{{
                    $t('admin.restriction_select')
                  }}</option>
                  <option
                    v-for="[type, type_title] in constants.security.restriction
                      .target_type"
                    :key="type"
                    :value="type"
                  >
                    {{ type_title }}
                  </option>
                </select>
              </i18n>
            </td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                v-if="target.seq"
                href="#"
                :class="[$style.button]"
                @click="handleSubmitTarget(idx)"
                ><span>{{ $t('admin.modify') }}</span></a
              >
              <a
                v-else
                href="#"
                :class="[$style.black, $style.button]"
                @click="handleSubmitTarget(idx)"
                ><span>{{ $t('admin.enroll') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.red, $style.button]"
                @click="handleDeleteTarget(idx)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!restriction_targets.length">
            <td :class="$style.tCenter" colspan="3">{{
              $t('admin.no_restriction_target')
            }}</td>
          </tr>
        </tbody>
      </table>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
